import { useState, useRef, useMemo } from 'react';
import { useWizardNavigation } from './use-wizard';
import { StepContainer } from './step-container';
import { Calendar, Check } from 'lucide-react';
import { DateFormat, formatDate } from '@/data/format/format-date';
import type { GroupPhaseWindow } from '@repo/shared-types';

interface WindowsByDate {
  [date: string]: GroupPhaseWindow[];
}

function StepSchedule() {
  const {
    searchParams,
    goNext,
    goBack,
    currentIndex,
    totalSteps,
    groupPhaseWindows,
    minRequiredInterests,
  } = useWizardNavigation();

  const initialWindows =
    searchParams.get('horarios')?.split(',').filter(Boolean) || [];
  const [selectedWindows, setSelectedWindows] =
    useState<string[]>(initialWindows);
  const autoAdvanceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isAutoAdvancing, setIsAutoAdvancing] = useState(false);

  const windowsByDate = useMemo(() => {
    const grouped: WindowsByDate = {};
    for (const window of groupPhaseWindows) {
      const dateKey = formatDate(window.start, DateFormat.DD_MM_YYYY);
      if (!grouped[dateKey]) {
        grouped[dateKey] = [];
      }
      grouped[dateKey].push(window);
    }
    for (const date in grouped) {
      grouped[date].sort(
        (a, b) => new Date(a.start).getTime() - new Date(b.start).getTime()
      );
    }
    return grouped;
  }, [groupPhaseWindows]);

  const isValidSelection = selectedWindows.length >= minRequiredInterests;

  const toggleWindow = (windowId: string) => {
    // Clear any pending auto-advance
    if (autoAdvanceTimer.current) {
      clearTimeout(autoAdvanceTimer.current);
      autoAdvanceTimer.current = null;
      setIsAutoAdvancing(false);
    }

    const isAdding = !selectedWindows.includes(windowId);
    const newSelection = isAdding
      ? [...selectedWindows, windowId]
      : selectedWindows.filter((id) => id !== windowId);

    setSelectedWindows(newSelection);

    // Auto-advance when minimum reached by adding a slot
    if (isAdding && newSelection.length >= minRequiredInterests) {
      setIsAutoAdvancing(true);
      autoAdvanceTimer.current = setTimeout(() => {
        goNext({ horarios: newSelection.join(',') });
      }, 1000);
    }
  };

  const handleNext = () => {
    if (autoAdvanceTimer.current) {
      clearTimeout(autoAdvanceTimer.current);
    }
    goNext({ horarios: selectedWindows.join(',') });
  };

  const remaining = minRequiredInterests - selectedWindows.length;

  return (
    <StepContainer
      stepIndex={currentIndex}
      totalSteps={totalSteps}
      title="¿En qué horarios pueden jugar?"
      description={
        isAutoAdvancing
          ? '¡Perfecto! Avanzando...'
          : isValidSelection
            ? `¡Listo! Seleccionaste ${selectedWindows.length} horario${selectedWindows.length !== 1 ? 's' : ''}.`
            : `Seleccioná al menos ${minRequiredInterests} horario${minRequiredInterests !== 1 ? 's' : ''} disponible${minRequiredInterests !== 1 ? 's' : ''}.`
      }
      onBack={goBack}
      onNext={handleNext}
      showNext={isValidSelection && !isAutoAdvancing}
      nextLabel="Continuar"
    >
      <div className="space-y-5">
        {/* Counter */}
        <div className="flex items-center gap-3">
          <div
            className={`text-sm font-medium px-3 py-1.5 rounded-full transition-colors ${
              isValidSelection
                ? 'bg-green-100 text-green-700'
                : 'bg-orange-100 text-orange-700'
            }`}
          >
            {isValidSelection ? (
              <span className="flex items-center gap-1">
                <Check className="w-3.5 h-3.5" />
                {selectedWindows.length} seleccionado
                {selectedWindows.length !== 1 ? 's' : ''}
              </span>
            ) : (
              <span>
                {selectedWindows.length} / {minRequiredInterests} mínimo
              </span>
            )}
          </div>
          {remaining > 0 && (
            <span className="text-sm text-gray-400">
              Falta{remaining !== 1 ? 'n' : ''} {remaining}
            </span>
          )}
        </div>

        {/* Time slots grouped by date */}
        {Object.entries(windowsByDate).map(([date, windows]) => (
          <div key={date}>
            <div className="flex items-center gap-2 mb-2">
              <Calendar className="w-4 h-4 text-gray-400" />
              <span className="text-sm font-semibold text-gray-600 uppercase tracking-wide">
                {date}
              </span>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {windows.map((window) => {
                const isSelected = selectedWindows.includes(window.windowId);
                const startTime = formatDate(window.start, DateFormat.HH_MM);
                const endTime = formatDate(window.end, DateFormat.HH_MM);

                return (
                  <button
                    key={window.windowId}
                    type="button"
                    onClick={() => toggleWindow(window.windowId)}
                    className={`flex items-center justify-between p-3.5 rounded-xl border-2 transition-all active:scale-[0.97] ${
                      isSelected
                        ? 'border-primary bg-primary/5 ring-2 ring-primary/10'
                        : 'border-gray-200 hover:border-gray-300 bg-white'
                    }`}
                  >
                    <div className="text-left">
                      <span className="text-sm font-semibold text-gray-900">
                        {startTime} - {endTime}
                      </span>
                      <span className="block text-xs text-gray-400">
                        {window.availableCount}{' '}
                        {window.availableCount === 1 ? 'cancha' : 'canchas'}
                      </span>
                    </div>
                    {isSelected && (
                      <Check className="w-4 h-4 text-primary flex-shrink-0" />
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </StepContainer>
  );
}

export const route = {
  path: 'horarios',
  element: <StepSchedule />,
};
